// Environment configuration
export const config = {
  API_BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
  PORTAL_URL: process.env.REACT_APP_PORTAL_URL || 'http://localhost:3001',
  PORTAL_API_URL:
    process.env.REACT_APP_PORTAL_API_URL || 'http://localhost:5001/api',
  ENV: process.env.REACT_APP_ENV || 'development',
  APP_NAME: process.env.REACT_APP_APP_NAME || 'Procurement Dashboard',
};

// Direct-from-CDN base for object-storage files. Public assets (keys that
// start with `public/`, e.g. product images) are served straight from
// MinIO behind Cloudflare — the backend never touches the bytes. Env var
// wins; otherwise derived from the API host. Empty (localhost) falls back
// to the backend /api/gridfs route, which still works for every reference.
export const FILES_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_FILES_BASE_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  try {
    const host = new URL(config.API_BASE_URL).host;
    if (host.startsWith('dev-api.'))
      return 'https://dev-files.gordiananalytics.com';
    if (host.startsWith('rfq2-api.'))
      return 'https://files.gordiananalytics.com';
  } catch {
    /* relative/invalid API URL — keep backend-proxied serving */
  }
  return '';
})();

// API endpoints
export const API_ENDPOINTS = {
  BASE_URL: config.API_BASE_URL,
  USERS: `${config.PORTAL_API_URL}/users`,
  CHANGE_PASSWORD: `${config.PORTAL_API_URL}/users/change-password`,
  COMPANIES: `${config.API_BASE_URL}/v2/companies`,
  COMPANY_NAME: (company_id: string) =>
    `${config.API_BASE_URL}/v2/companies/${company_id}/name`,
  COMPANY_DETAILS: (company_id: string) =>
    `${config.API_BASE_URL}/v2/companies/${company_id}`,
  SUPPLIERS_V2: `${config.API_BASE_URL}/v2/suppliers`,
  SUPPLIER_DETAILS: (supplier_id: string) =>
    `${config.API_BASE_URL}/v2/suppliers/${supplier_id}`,
  SUPPLIER_NAME_SUGGEST: `${config.API_BASE_URL}/v2/suppliers/suggest-by-name`,
  // Products V2 endpoints
  PRODUCTS_V2: `${config.API_BASE_URL}/v2/products`,
  // Fast server-side "export all pages" (bypasses the list endpoint's per-row
  // N+1; returns the file directly). Scope travels as query params, columns in body.
  PRODUCTS_GROUPED: `${config.API_BASE_URL}/v2/products/grouped`,
  // Caller company's own product-category labels (company_approvals.company_category),
  // company-scoped. Powers the marketplace category tiles.
  PRODUCTS_MY_CATEGORIES: `${config.API_BASE_URL}/v2/products/my-categories`,
  PRODUCT_VARIANTS: (variant_id: string) =>
    `${config.API_BASE_URL}/v2/products/variants/${variant_id}`,
  PRODUCT_DETAILS: (product_id: string) =>
    `${config.API_BASE_URL}/v2/products/${product_id}`,
  // Canonical-row edits on the merged company-products grid go here (a canonical
  // can't be PATCHed via the products endpoint).
  CANONICAL_DETAILS: (canonical_id: string) =>
    `${config.API_BASE_URL}/v2/canonical-products/${canonical_id}`,
  PRODUCT_MERGE: `${config.API_BASE_URL}/v2/products/merge`,
  PRODUCT_MERGE_PREVIEW: `${config.API_BASE_URL}/v2/products/merge/preview`,
  // Categories V2 endpoints
  REQUESTS_V2: `${config.API_BASE_URL}/v2/requests`,
  REQUESTS_V2_DELETED: `${config.API_BASE_URL}/v2/requests/deleted`,
  REQUEST_V2_RESTORE: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/restore`,
  REQUEST_V2_DETAIL: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}`,
  REQUEST_V2_PRODUCTS: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/products`,
  REQUEST_V2_MANUAL_PRODUCTS: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/products?is_manual=true`,
  REQUEST_PRODUCT_SIMILAR: (request_id: string, product_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/products/${product_id}/similar`,
  REQUEST_V2_MANUAL_PRODUCTS_ALL: `${config.API_BASE_URL}/v2/requests/manual`,
  REQUEST_COMMUNICATION_SUMMARY: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/summary`,
  REQUEST_COMMUNICATION_MESSAGES: (request_id: string, supplier_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/messages`,
  REQUEST_COMMUNICATION_CONTACTS: (request_id: string, supplier_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/contacts`,
  REQUEST_COMMUNICATION_READ: (request_id: string, supplier_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/read`,
  REQUEST_COMMUNICATION_AI_REPLY: (request_id: string, supplier_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/reply-with-ai`,
  REQUEST_COMMUNICATION_ATTACHMENT: (
    request_id: string,
    attachment_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/attachments/${attachment_id}`,
  REQUEST_QUOTES_SUMMARY: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/quotes-summary`,
  REQUEST_PURCHASE_ORDER: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/purchase-order`,
  /** Tek tedarikçinin sipariş maili ön izlemesi (POST, göndermez). */
  REQUEST_PURCHASE_ORDER_PREVIEW: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/purchase-order/preview`,
  /** Kademeli RFQ: strateji, açık dalga, dalga başına aday sayısı (GET). */
  REQUEST_SUPPLIER_WAVE: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/supplier-wave`,
  /** Kalem başına zaten sorulan satıcılar (iletişimdeki ekleme penceresi). */
  REQUEST_LINE_SUPPLIERS: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/line-suppliers`,
  /** Kademeli RFQ: dalgayı ilerlet, yedekleri / havuzu aç (POST). */
  REQUEST_EXPAND_SUPPLIER_WAVE: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/expand-supplier-wave`,
  /** Kalemin birincil satıcısını değiştir (POST, sipariş sonrası). */
  REQUEST_PRIMARY_SUPPLIER: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/primary-supplier`,
  /** Kalemin fiyat akışı: teklif geçmişi, kazananlar işaretli (GET). */
  REQUEST_PRODUCT_PRICE_FLOW: (
    request_id: string,
    request_product_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/products/${request_product_id}/price-flow`,
  /** Mesaja elle dosya ekleme (POST multipart, admin). */
  REQUEST_COMMUNICATION_MESSAGE_ATTACHMENTS: (
    request_id: string,
    supplier_id: string,
    message_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/messages/${message_id}/attachments`,
  /** Eki gizle / geri getir (PATCH, admin). */
  REQUEST_COMMUNICATION_MESSAGE_ATTACHMENT: (
    request_id: string,
    supplier_id: string,
    message_id: string,
    attachment_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/messages/${message_id}/attachments/${attachment_id}`,
  /** Tek mesaj: gorunen metni temizleme (PATCH, admin). */
  REQUEST_COMMUNICATION_MESSAGE: (
    request_id: string,
    supplier_id: string,
    message_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/messages/${message_id}`,
  REQUEST_COMMUNICATION_SUPPLIER_PRODUCTS: (
    request_id: string,
    supplier_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/supplier-products`,
  REQUEST_COMMUNICATION_ADD_SUPPLIER: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/add-supplier`,
  REQUEST_SUPPLIER_SELECTIONS: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/supplier-selections`,
  REQUEST_SUPPLIER_SELECTION_ENTRY: (request_id: string, supplier_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/supplier-selections/${supplier_id}`,
  REQUEST_SUPPLIER_NOT_SELLING: (request_id: string, supplier_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/supplier-selections/${supplier_id}/not-selling`,
  REQUEST_NEGOTIATION: (request_id: string, supplier_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/negotiation`,
  REQUEST_NEGOTIATION_SEND: (request_id: string, supplier_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/${supplier_id}/negotiation/send`,
  REQUEST_NEGOTIATIONS: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/negotiations`,
  REQUEST_NEGOTIATIONS_FINISH: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/negotiations/finish`,
  REQUEST_SUPPLIER_SELECTION_PRODUCTS: (
    request_id: string,
    supplier_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/supplier-selections/${supplier_id}/products`,
  REQUEST_SUPPLIER_AVAILABLE_PRODUCTS: (
    request_id: string,
    supplier_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/supplier-selections/${supplier_id}/available-products`,
  BRAVE_SUPPLIER_SEARCH: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/suppliers/brave-search`,
  BRAVE_SUPPLIER_SEARCH_PROGRESS: (
    request_id: string,
    request_product_id: string
  ) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/suppliers/brave-search/progress/${request_product_id}`,
  REQUEST_PHASE_UPDATE: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/phase`,
  REQUEST_USER_SUPPLIERS: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/user-suppliers`,
  REQUEST_SIMILAR_PRODUCTS: (request_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/similar-products`,
  // Stored-file URL for any reference generation: object-storage keys that
  // are public go straight to the CDN; private keys hit the backend route
  // (auth + 302 presigned); legacy GridFS ids stream from the backend.
  GRIDFS: (file_id: string) => {
    if (
      file_id &&
      file_id.includes('/') &&
      file_id.startsWith('public/') &&
      FILES_BASE_URL
    ) {
      return `${FILES_BASE_URL}/${file_id}`;
    }
    return `${config.API_BASE_URL.replace('/api', '')}/api/gridfs/${file_id}`;
  },
  USER_INBOX: `${config.API_BASE_URL}/v2/user-inbox`,
  USER_INBOX_ITEM: (inbox_id: string) =>
    `${config.API_BASE_URL}/v2/user-inbox/${inbox_id}`,
  USER_INBOX_MESSAGE: (inbox_id: string) =>
    `${config.API_BASE_URL}/v2/user-inbox/${inbox_id}/message`,
  USER_INBOX_UNREAD_BY_PHASE: `${config.API_BASE_URL}/v2/user-inbox/unread-counts-by-phase`,
  USER_INBOX_ADD_PARTICIPANTS: (inbox_id: string) =>
    `${config.API_BASE_URL}/v2/user-inbox/${inbox_id}/participants`,
  USER_INBOX_REMOVE_PARTICIPANT: (inbox_id: string, user_id: string) =>
    `${config.API_BASE_URL}/v2/user-inbox/${inbox_id}/participants/${user_id}`,
  USER_INBOX_UPLOAD: (inbox_id: string) =>
    `${config.API_BASE_URL}/v2/user-inbox/${inbox_id}/upload`,
  USER_INBOX_ATTACHMENT: (inbox_id: string, attachment_id: string) =>
    `${config.API_BASE_URL}/v2/user-inbox/${inbox_id}/attachment/${attachment_id}`,
  USER_INBOX_MARK_READ: `${config.API_BASE_URL}/v2/user-inbox/mark-read`,
  // Pre-request (Cart) endpoints
  PREREQUESTS: `${config.API_BASE_URL}/v2/prerequests`,
  PREREQUEST_ADD: `${config.API_BASE_URL}/v2/prerequests/add`,
  PREREQUEST_UPDATE: `${config.API_BASE_URL}/v2/prerequests/update`,
  PREREQUEST_REMOVE: `${config.API_BASE_URL}/v2/prerequests/remove`,
  PREREQUEST_CLEAR: `${config.API_BASE_URL}/v2/prerequests/clear`,
  // RFQ line-item attachments (photos/files for manual products)
  LINE_ATTACHMENTS: `${config.API_BASE_URL}/v2/line-attachments`,
  // Supplier Products V2 endpoints
  SUPPLIER_PRODUCTS_V2: `${config.API_BASE_URL}/v2/supplier-products`,
  SUPPLIER_PRODUCT_DETAIL: (supplier_product_id: string) =>
    `${config.API_BASE_URL}/v2/supplier-products/${supplier_product_id}`,
  // Search endpoints (V2)
  EXPORT_DATA: `${config.API_BASE_URL}/v2/export/data`,
  EXPORT_ROWS: `${config.API_BASE_URL}/v2/export/rows`,
  // Seçilen RFQ'ların Özet sayfaları, tek çok sayfalı Excel.
  EXPORT_RFQ_OVERVIEWS: `${config.API_BASE_URL}/v2/export/rfq-overviews`,
  EXPORT_FIELDS: (collection: string) =>
    `${config.API_BASE_URL}/v2/export/fields/${collection}`,
  // Analytics endpoints (V2)
  COMPANY_SAVINGS: (company_id: string) =>
    `${config.API_BASE_URL}/v2/analytics/companies/${company_id}/savings`,
  COMPANY_TOTAL_SPEND_PIPELINE: (company_id: string) =>
    `${config.API_BASE_URL}/v2/analytics/companies/${company_id}/total-spend-pipeline`,
  COMPANY_COMMITTED_SPEND: (company_id: string) =>
    `${config.API_BASE_URL}/v2/analytics/companies/${company_id}/committed-spend`,
  COMPANY_YEARLY_OVERVIEW: (company_id: string) =>
    `${config.API_BASE_URL}/v2/analytics/companies/${company_id}/yearly-overview`,
  // Debug endpoint removed (not available in V2)
  // COMPANY_REQUESTS_DEBUG: (company_id: string) => `${config.API_BASE_URL}/companies/${company_id}/requests-debug`,
  REQUEST_MAILBOX_FINISH: (request_id: string, mailbox_id: string) =>
    `${config.API_BASE_URL}/v2/requests/${request_id}/communication/mailboxes/${mailbox_id}/finish`,
  SUPPLIER_PRODUCTS_COMMIT_DRAFTS: `${config.API_BASE_URL}/v2/supplier-products/commit-drafts`,
  AGENTIC_LOGS: `${config.API_BASE_URL}/v2/agentic/logs`,
  REQUEST_AUDIT_LOGS: `${config.API_BASE_URL}/v2/audit-logs/requests`,

  // Mailgun endpoints
  COMPANY_ADDRESSES: (company_id: string) =>
    `${config.API_BASE_URL}/v2/addresses/companies/${company_id}/addresses`,
  COMPANY_ADDRESS: (company_id: string, address_id: string) =>
    `${config.API_BASE_URL}/v2/addresses/companies/${company_id}/addresses/${address_id}`,
  COMPANY_ADDRESS_DEFAULT: (company_id: string, address_id: string) =>
    `${config.API_BASE_URL}/v2/addresses/companies/${company_id}/addresses/${address_id}/set-default`,
  // PDF endpoints
  AI_ACTIONS: `${config.API_BASE_URL}/v2/ai-actions`,
  AI_ACTION_DETAIL: (id: string) =>
    `${config.API_BASE_URL}/v2/ai-actions/${id}`,
  AI_ACTION_ATTACHMENTS: (id: string) =>
    `${config.API_BASE_URL}/v2/ai-actions/${id}/attachments`,
  AI_ACTION_SEND: (id: string) =>
    `${config.API_BASE_URL}/v2/ai-actions/${id}/send`,
  AI_ACTION_DISCARD: (id: string) =>
    `${config.API_BASE_URL}/v2/ai-actions/${id}/discard`,
  AI_ACTION_REGENERATE: (id: string) =>
    `${config.API_BASE_URL}/v2/ai-actions/${id}/regenerate`,
  AI_ACTION_REMINDER_STATUS: `${config.API_BASE_URL}/v2/ai-actions/reminder-status`,
  AI_ACTION_REMINDER_TRIGGER: `${config.API_BASE_URL}/v2/ai-actions/reminder-trigger`,
  AI_ACTION_SEED_INITIAL_OUTREACH: `${config.API_BASE_URL}/v2/ai-actions/seed-initial-outreach`,
  AI_ACTION_SEED_FRAME_CONTRACT: `${config.API_BASE_URL}/v2/ai-actions/seed-frame-contract`,
  AI_ACTION_AUTOMATION_START: `${config.API_BASE_URL}/v2/ai-actions/automation/start`,
  AI_ACTION_AUTOMATION_STOP: `${config.API_BASE_URL}/v2/ai-actions/automation/stop`,
  AI_ACTION_THREAD_UNANSWERED: `${config.API_BASE_URL}/v2/ai-actions/thread/unanswered`,
  AI_ACTION_THREAD_GENERATE_FOR_EMAIL: `${config.API_BASE_URL}/v2/ai-actions/thread/generate-for-email`,
  AI_ACTION_THREAD_MARK_REPLIED: `${config.API_BASE_URL}/v2/ai-actions/thread/mark-replied`,
  AI_ACTION_THREAD_UNMARK_REPLIED: `${config.API_BASE_URL}/v2/ai-actions/thread/unmark-replied`,

  // Human-attention queue (Action Center paneli Mail Dashboard'da yaşar).
  ATTENTION_RESOLVE: (id: string) =>
    `${config.API_BASE_URL}/v2/attention/${id}/resolve`,

  // Mail Dashboard (Gordian admin) — trafik, edge-case başlıkları, unmatched.
  MAIL_DASHBOARD_OVERVIEW: `${config.API_BASE_URL}/v2/admin/mail-dashboard/overview`,
  MAIL_DASHBOARD_TOPICS: `${config.API_BASE_URL}/v2/admin/mail-dashboard/attention-topics`,
  MAIL_DASHBOARD_TOPIC_DETAIL: (type: string) =>
    `${config.API_BASE_URL}/v2/admin/mail-dashboard/attention-topics/${type}`,
  MAIL_DASHBOARD_UNMATCHED: `${config.API_BASE_URL}/v2/admin/mail-dashboard/unmatched`,
  MAIL_DASHBOARD_UNMATCHED_DETAIL: (id: string) =>
    `${config.API_BASE_URL}/v2/admin/mail-dashboard/unmatched/${id}`,
  MAIL_DASHBOARD_UNMATCHED_DISMISS: (id: string) =>
    `${config.API_BASE_URL}/v2/admin/mail-dashboard/unmatched/${id}/dismiss`,
  MAIL_DASHBOARD_UNMATCHED_ANALYZE: (id: string) =>
    `${config.API_BASE_URL}/v2/admin/mail-dashboard/unmatched/${id}/analyze`,
  MAIL_DASHBOARD_UNMATCHED_ATTACH: (id: string) =>
    `${config.API_BASE_URL}/v2/admin/mail-dashboard/unmatched/${id}/attach`,
  MAIL_DASHBOARD_ATTACH_TARGETS: `${config.API_BASE_URL}/v2/admin/mail-dashboard/unmatched/attach-targets`,
  MAIL_DASHBOARD_ATTACH_PREVIEW: `${config.API_BASE_URL}/v2/admin/mail-dashboard/unmatched/attach-preview`,
  MAIL_DASHBOARD_MESSAGES: `${config.API_BASE_URL}/v2/admin/mail-dashboard/messages`,
  MAIL_DASHBOARD_MISCALC: `${config.API_BASE_URL}/v2/admin/mail-dashboard/miscalc`,
  MAIL_DASHBOARD_ACTION_CENTER: `${config.API_BASE_URL}/v2/admin/mail-dashboard/action-center`,
  MAIL_DASHBOARD_COMPANIES: `${config.API_BASE_URL}/v2/admin/mail-dashboard/companies`,
  MAIL_DASHBOARD_CUSTOMER_ACTIONS: `${config.API_BASE_URL}/v2/admin/mail-dashboard/customer-actions`,
  MAIL_DASHBOARD_CONVERSATION: `${config.API_BASE_URL}/v2/admin/mail-dashboard/conversation`,
};

// Development helpers
export const isDevelopment = config.ENV === 'development';
export const isProduction = config.ENV === 'production';
